import * as React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { useMemo, useState, useCallback } from 'react';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Stack, Avatar, ButtonBase, Typography } from '@mui/material';

import { paths, ROOTS } from 'src/routes/paths';

import { useEntityContext } from 'src/entity/hooks';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

function groupByParent(data) {
  const parents = {};
  const noParent = {};

  // Identifier les parents et initialiser leur structure avec un tableau 'childs'
  data.forEach((item) => {
    if (item.parent === null) {
      // Traiter les éléments sans parent
      noParent[item.id] = item;
      // Vérifier si le parent existe dans les données
    } else if (data.some((parent) => parent.id === item.parent)) {
      // Si le parent existe, le préparer avec un tableau 'childs'
      if (!parents[item.parent]) {
        const parentObj = data.find((parent) => parent.id === item.parent);
        if (parentObj) {
          parents[item.parent] = { ...parentObj, childs: [] };
        }
      }
      // Ajouter l'élément au tableau 'childs' du parent correspondant
      parents[item.parent].childs.push(item);
    } else {
      // Si aucun parent correspondant n'est trouvé, traiter comme sans parent
      noParent[item.id] = item;
    }
  });

  // Fusionner les parents et les éléments sans parents
  const result = { ...noParent, ...parents };

  return result;
}

// ----------------------------------------------------------------------

export default function MyEntitiesMenu({ children, sx, ...other }) {
  const { entity: currentEntity, myEntities } = useEntityContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const location = useLocation();

  const groupedByParent = useMemo(() => groupByParent(myEntities), [myEntities]);
  const groupedByParentKeys = useMemo(() => Object.keys(groupedByParent), [groupedByParent]);

  const getUpdatedUrl = useCallback(
    (newId, newParent) => {
      // Récupérer les segments du chemin actuel
      const pathSegments = location.pathname.split('/').filter(Boolean);

      // Identifier le module actuel (ges, engagement, diag_ref)
      const currentModule = pathSegments.find((segment) =>
        [
          ROOTS.GES_MODULE.substring(1),
          ROOTS.ENGAGEMENT_MODULE.substring(1),
          ROOTS.DIAG_REF_MODULE.substring(1),
        ].includes(segment)
      );

      // Si nous sommes sur la page des modules, rediriger vers la page des modules de la nouvelle entité
      if (pathSegments.includes('modules') && !currentModule) {
        return paths.entity.modules.root(newId, newParent);
      }

      // Récupérer l'entité cible
      const targetEntity = myEntities.find((e) => e.id === newId);

      // Si nous sommes dans un module spécifique
      if (currentModule && targetEntity) {
        // Vérifier si l'entité a accès au module actuel
        const canAccessModule = {
          ges: targetEntity.can_access_ges,
          engagement: targetEntity.can_access_engagement,
          diag_ref: targetEntity.can_access_diag_ref,
        }[currentModule];

        if (canAccessModule) {
          // Si l'entité a accès au module actuel, garder le même chemin en remplaçant juste l'ID
          const moduleRoot = {
            ges: paths.entity.modules.ges.dashboard.root,
            engagement: paths.entity.modules.engagement.dashboard.root,
            diag_ref: paths.entity.modules.diag_ref.dashboard.root,
          }[currentModule];

          return moduleRoot(newId, newParent);
        }
      }

      // Par défaut, rediriger vers la page des modules
      return paths.entity.modules.root(newId, newParent);
    },
    [location.pathname, myEntities]
  );

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const RenderCheckmark = useCallback(
    ({ entityId }) => {
      if (!currentEntity) return null;
      if (entityId === currentEntity.id) return <Iconify icon="material-symbols:check" />;
      return null;
    },
    [currentEntity]
  );

  return (
    <>
      <ButtonBase
        sx={sx}
        id="myentities-button"
        aria-controls={open ? 'myentities-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        {...other}
      >
        {children}
      </ButtonBase>
      <Menu
        id="myentities-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'myentities-button',
        }}
      >
        {groupedByParentKeys.map((entityid) => {
          const { id, name = '', parent, logo = 'test' } = groupedByParent[entityid];

          return (
            <MenuItem
              component="a"
              href={getUpdatedUrl(id, parent)}
              key={id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack gap={1} alignItems="center" direction="row">
                <Avatar
                  alt={name}
                  src={logo}
                  sx={{ width: 24, height: 24 }}
                  variant="square"
                  imgProps={{ style: { objectFit: 'contain' } }}
                >
                  {name[0] || '?'}
                </Avatar>
                <Typography variant="body2">{name}</Typography>
                <RenderCheckmark entityId={id} />
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

MyEntitiesMenu.propTypes = {
  sx: PropTypes.object,
  children: PropTypes.node,
};
